import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "app/components/ui/card";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import { startOfDay, addDays, differenceInDays } from "date-fns";
import { Button } from "app/components/ui/button";
import { useState } from "react";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
} from "app/components/ui/chart";
import type { ChartConfig } from "app/components/ui/chart";
import type { GetActiveLoansByMaturityQuery } from "~/types/graphql";

const formatUSD = (value: number) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const chartConfig = {
  gte121Days: {
    label: ">= 121 Days",
    color: "hsl(var(--chart-1))",
  },
  lt121Days: {
    label: "30-120 Days",
    color: "hsl(var(--chart-2))",
  },
  lt30Days: {
    label: "<30 Days",
    color: "hsl(var(--chart-3))",
  },
  expired: {
    label: "Expired",
    color: "hsl(var(--chart-4))",
  },
} satisfies ChartConfig;

const LoanMaturityChart = ({
  loans,
}: {
  loans: GetActiveLoansByMaturityQuery["coolerLoans"];
}) => {
  const [showValues, setShowValues] = useState(false);

  // Get the start of today
  const today = startOfDay(new Date());

  // Create a 120-day window of future data
  const chartData = Array.from({ length: 121 }).map((_, i) => {
    const date = addDays(today, i);

    // Categories for this date
    const categories = loans?.reduce(
      (acc, loan) => {
        const expiryDate = new Date(
          parseInt(loan.currentExpiryTimestamp) * 1000
        );
        const daysFromTodayToExpiry = differenceInDays(expiryDate, today);
        const daysIntoFuture = i;

        // Use principalPayable from latest repayment if it exists, otherwise use loan principal

        const principal =  Number(loan.principal);

        // Calculate remaining days at this future point
        const remainingDays = daysFromTodayToExpiry - daysIntoFuture;

        // Categorize loans based on remaining days
        if (remainingDays < 0) {
          acc.expired += 1;
          acc.expiredValue += principal;
        } else if (remainingDays < 30) {
          acc.lt30Days += 1;
          acc.lt30DaysValue += principal;
        } else if (remainingDays < 121) {
          acc.lt121Days += 1;
          acc.lt121DaysValue += principal;
        } else {
          acc.gte121Days += 1;
          acc.gte121DaysValue += principal;
        }

        return acc;
      },
      {
        gte121Days: 0,
        lt121Days: 0,
        lt30Days: 0,
        expired: 0,
        gte121DaysValue: 0,
        lt121DaysValue: 0,
        lt30DaysValue: 0,
        expiredValue: 0,
      }
    );

    return {
      date: date.toISOString().split("T")[0],
      ...categories,
    };
  });

  const formatYAxis = (value: number) => {
    return showValues ? formatUSD(value) : value.toString();
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Projected Loan Maturities</CardTitle>
        <Button variant="outline" onClick={() => setShowValues(!showValues)}>
          Show {showValues ? "Counts" : "Values"}
        </Button>
      </CardHeader>
      <CardContent className="pt-0">
        <ChartContainer config={chartConfig}>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart
              data={chartData}
              margin={{ top: 0, right: 10, bottom: 0, left: 12 }}
            >
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={(value) => value.split("-").slice(1).join("/")}
                interval={14}
                tickLine={false}
                axisLine={false}
              />
              <YAxis tickFormatter={formatYAxis} />
              <ChartTooltip content={<ChartTooltipContent />} />
              <ChartLegend content={<ChartLegendContent />} />
              {Object.entries(chartConfig).map(([key, config]) => (
                <Bar
                  key={key}
                  name={config.label}
                  dataKey={showValues ? `${key}Value` : key}
                  stackId="a"
                  fill={`var(--color-${key})`}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
      </CardContent>
    </Card>
  );
};

export default LoanMaturityChart;
